/**
 * Геопозиция для тревоги. Два потребителя:
 *  - аварийный маршрут «от моего места до ближайшего укрытия» (запрос
 *    владельца 27.07) — `ensureGeoStart`: уже известную позицию не
 *    перезапрашиваем, промпт лишний раз не дёргаем;
 *  - решение «где человек» (24.09.2026, alarm-where.ts) — нужен фикс С
 *    ТОЧНОСТЬЮ и временем: `geoFix`. Обновляет его alarm-position.ts и кнопка
 *    «Обновить место» (`refreshPosition`).
 * Позиция живёт только в памяти вкладки — никуда не пишется и не шлётся.
 */
import type { LonLat } from '@app/geo'
import { geoFix, geoStart } from '../state'

/** Минимум полей GeolocationPosition, который нам нужен (и который легко подделать в тесте). */
export interface GeoReading {
  readonly coords: {
    readonly longitude: number
    readonly latitude: number
    readonly accuracy: number
  }
  readonly timestamp: number
}

/** Запомнить фикс: и точку для маршрута (geoStart), и фикс с точностью (geoFix). */
export function storeFix(pos: GeoReading): LonLat {
  const p: LonLat = [pos.coords.longitude, pos.coords.latitude]
  geoStart.value = p
  // timestamp браузера бывает из кэша ОС (maximumAge) — берём его, а не «сейчас»:
  // возраст фикса честный, старый фикс не выдаётся за свежий.
  const at = Number.isFinite(pos.timestamp) && pos.timestamp > 0 ? pos.timestamp : Date.now()
  const accuracyM = Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : Infinity
  geoFix.value = { point: p, accuracyM, at }
  return p
}

/**
 * Ошибка геолокации. Отказ в разрешении — позиции нет совсем (geoFix = null,
 * «где человек» решится по QR или вопросу). Таймаут и «нет сигнала» старый
 * фикс не стирают: он честно стареет (presence.ts добавляет к точности
 * 1,4 м/с после 2 минут и отбрасывает после 10) — это не отказ, а пауза.
 */
export function onGeoError(err: { readonly code: number }): void {
  if (err.code === 1) geoFix.value = null
}

/** Ждём геопозицию: кэш → getCurrentPosition (с промптом). null — нет/отказ. */
export function ensureGeoStart(): Promise<LonLat | null> {
  if (geoStart.value) return Promise.resolve(geoStart.value)
  return refreshPosition()
}

/**
 * Запросить позицию сейчас (начало тревоги, возврат на вкладку, кнопка
 * «Обновить место»). Точный режим, кэш ОС не старше 30 с. null — нет позиции.
 */
export function refreshPosition(): Promise<LonLat | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(storeFix(pos)),
      (err) => {
        onGeoError(err)
        resolve(null)
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 8_000 },
    )
  })
}
